import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ArrowLeft, Check } from 'lucide-react';

const PricingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(1);

  const planDetails = {
    1: {
      basic: '599 Rs',
      pro: '2999 Rs',
      enterprise: '5999 Rs',
    },
    2: {
      basic: '1599 Rs',
      pro: '5999 Rs',
      enterprise: '8999 Rs',
    },
    3: {
      basic: '4599 Rs',
      pro: '8999 Rs',
      enterprise: '12999 Rs',
    },
  };

  const basicFeatures = [
    'Users (Limit 5)',
    'WhatsApp Onboarding',
    'WhatsApp Green Tick Verification Assistance',
    'Meta Business Verification Assistance',
    'Custom Domain',
    'Quick Replies',
    'Template messages',
    'Trigger Flow from Chat',
    "FAQ's",
    'Broadcasts',
    'chat\\bot (Limit 10)',
    'Test and preview bot',
    'Custom Notifications',
    'Opt-in and opt-out user consent',
    'Live Chat',
    'Conversation reports',
    'Export Reports',
    'WhatsApp Bot Support (24/7)',
    'Email Support (24/7)',
    'WhatsApp Live Support (24/7)',
    'Priority Support (24/7)',
    'Dedicated Manager',
  ];

  const proFeatures = [
    'Users (Limit 10)',
    'WhatsApp Onboarding',
    'WhatsApp Green Tick Verification Assistance',
    'Meta Business Verification Assistance',
    'Custom Domain',
    'Quick Replies',
    'Template messages',
    'Trigger Flow from Chat',
    "FAQ's",
    'chat\\bot (Limit 25)',
    'Test and preview bot',
    'Custom Notifications',
    'Opt-in and opt-out user consent',
    'Live Chat',
    'Broadcasts',
    'Conversation reports',
    'Export Reports',
    'WhatsApp Bot Support (24/7)',
    'Email Support (24/7)',
    'WhatsApp Live Support (24/7)',
    'Priority Support (24/7)',
    'Dedicated Manager',
  ];

  const enterpriseFeatures = [
    'Users (Custom)',
    'WhatsApp Onboarding',
    'WhatsApp Green Tick Verification Assistance',
    'Meta Business Verification Assistance',
    'Custom Domain',
    'Quick Replies',
    'Export contacts',
    'Template messages',
    'Export Chats',
    'Trigger Flow from Chat',
    "FAQ's",
    'Test and preview bot',
    'Custom Notifications',
    'Opt-in and opt-out user consent',
    'Live Chat',
    'Broadcasts',
    'chat\\bot (Limit 50)',
    'Conversation reports',
    'Export Reports',
    'WhatsApp Bot Support (24/7)',
    'Email Support (24/7)',
    'WhatsApp Live Support (24/7)',
    'Priority Support (24/7)',
    'Integrations',
    'Dedicated Manager',
  ];

  const planOptions = [
    { value: 1, label: 'Monthly', note: 'Additional 2%' },
    { value: 2, label: 'Quarterly', note: 'Additional 5%' },
    { value: 3, label: 'Yearly', note: 'Additional 10%' },
  ];

  const cards = [
    {
      title: 'Basic',
      price: planDetails[selectedPlan].basic,
      features: basicFeatures,
      bg: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)',
    },
    {
      title: 'Pro',
      price: planDetails[selectedPlan].pro,
      features: proFeatures,
      bg: 'linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%)',
    },
    {
      title: 'Enterprise',
      price: planDetails[selectedPlan].enterprise,
      features: enterpriseFeatures,
      bg: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={18} />}
          href="/"
          sx={{ minWidth: 0, px: 1 }}
        >
          Back
        </Button>
        <Typography variant="h5">Plan Details</Typography>
      </Stack>

      <Stack alignItems="center" mb={3}>
        <ToggleButtonGroup
          value={selectedPlan}
          exclusive
          onChange={(_, val) => val && setSelectedPlan(val)}
          color="primary"
          size="small"
        >
          {planOptions.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} sx={{ px: 3 }}>
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="subtitle2">{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.note}
                </Typography>
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} md={4} key={card.title}>
            <Card
              sx={{
                height: '100%',
                background: card.bg,
                borderRadius: 2,
                boxShadow: 4,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'scale(0.99)', boxShadow: 6 },
              }}
            >
              <CardContent>
                <Typography variant="h6" textAlign="center" gutterBottom>
                  {card.title}
                </Typography>
                <Typography
                  variant="h5"
                  color="primary"
                  textAlign="center"
                  gutterBottom
                >
                  Price: {card.price}
                </Typography>
                <Stack component="ul" spacing={0.75} sx={{ pl: 0, listStyle: 'none', color: 'text.primary' }}>
                  {card.features.map((feature, idx) => (
                    <Stack
                      key={idx}
                      component="li"
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Check size={14} color="#2ab63f" />
                      <Typography variant="body2">{feature}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="center" mt={2}>
                  <Button variant="outlined" color="primary">
                    Choose
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PricingPage;
